import Spacer, {
  ESpacerWeight,
} from "@rentiohq/shared-frontend/dist/components/components/Spacer";
import * as brokerHooks from "@rentiohq/shared-frontend/dist/redux/broker/broker.hooks";
import { useCount } from "@rentiohq/shared-frontend/dist/redux/count/count.hooks";
import * as bankAccountHooks from "@rentiohq/shared-frontend/dist/reduxV2/bankAccount/bankAccount.hooks";
import { payInModule } from "@rentiohq/shared-frontend/dist/reduxV2/payin";
import * as paymentRequestHooksV2 from "@rentiohq/shared-frontend/dist/reduxV2/paymentRequest/paymentRequest.hooks";
import { EBrokerFeature } from "@rentiohq/shared-frontend/dist/types/broker.types";
import { append } from "@rentiohq/shared-frontend/dist/utils/api.utils";
import { getLocalizedText } from "@rentiohq/shared-frontend/dist/utils/i18n/i18n.utils";
import {
  Banner,
  Link,
  Loading,
  Page,
  TextStyle,
} from "@rentiohq/web-shared/dist/components";
import { TabAction } from "@rentiohq/web-shared/dist/types";
import { matchPath } from "react-router";
import { useLocation } from "react-router-dom";
import { PayInsFollowUp } from "scenes/Properties/scenes/Payments/components/PayInsFollowUp";
import { getPayInFilter } from "scenes/Properties/scenes/Payments/components/PayInsFollowUp/PayInsFollowUp";
import { OpenPaymentRequests } from "../../../components/OpenPaymentRequests";
import { ts } from "../../../services";
import { CollectionFollowUp as CollectionFollowUpV2 } from "../../Properties/scenes/Payments/components/CollectionFollowUpV2";
import {
  EPaymentRequestFilterHidden,
  getPaymentRequestsFilter,
  getPaymentRequestsFilterV2,
} from "./Payments.utils";

const FOLLOW_UP_SUB_ROUTE = "follow-up";
const PAYMENTS_SUB_ROUTE = "payments";

const PATH_PAYMENTS = `/${FOLLOW_UP_SUB_ROUTE}/${PAYMENTS_SUB_ROUTE}`;

const PATH_OPEN_PAYMENTS = `${PATH_PAYMENTS}/open-payments`;
const PATH_UNMATCHED_PAY_INS = `${PATH_PAYMENTS}/unmatched-pay-ins`;
const PATH_MISSING_KYCS = `${PATH_PAYMENTS}/missing-kycs`;

export const Payments = () => {
  // Hooks
  const location = useLocation();

  const hasPaymentV2ForBroker = brokerHooks.useBrokerFeature(
    EBrokerFeature.PaymentV2,
  );

  const hasIbanisationForBroker = brokerHooks.useBrokerFeature(
    EBrokerFeature.Ibanisation,
  );

  const { count: paymentRequestsCount } = useCount({
    shouldRefetch: false,
    countBase: !hasPaymentV2ForBroker
      ? append("/payment-requests/count", {
          where: getPaymentRequestsFilter({}).where,
        })
      : undefined,
  });

  const { count: paymentRequestsCountV2 } = paymentRequestHooksV2.useCount({
    shouldRefetch: false,
    query: getPaymentRequestsFilterV2({
      hidden: EPaymentRequestFilterHidden.NotHidden,
    }),
  });

  const { count: payInsCountV2 } = payInModule.hooks.useCount({
    shouldRefetch: true,
    query: {
      filter: getPayInFilter(),
    },
  });

  const {
    count: missingKycsCountV2,
    isFetching: isFetchingMissingKycsCountV2,
  } = bankAccountHooks.useCount({
    shouldRefetch: true,
    query: {},
    customPath: "/bank-accounts/missing-kycs/count",
  });

  const getTabs = () => {
    const tabs: TabAction[] = [];

    tabs.push({
      content: getLocalizedText("payments.follow_up.payment_requests.heading"),
      url: PATH_PAYMENTS,
      isActive: !!matchPath(PATH_PAYMENTS, location.pathname),
      count: hasPaymentV2ForBroker
        ? paymentRequestsCountV2
        : paymentRequestsCount,
    });

    if (hasIbanisationForBroker) {
      tabs.push({
        content: getLocalizedText("follow_up.unmatched_pay_ins.title"),
        url: PATH_UNMATCHED_PAY_INS,
        isActive: !!matchPath(PATH_UNMATCHED_PAY_INS, location.pathname),
        count: payInsCountV2,
      });
    }

    tabs.push({
      content: getLocalizedText("payments.collection.follow_up.heading"),
      url: PATH_MISSING_KYCS,
      isActive: !!matchPath(PATH_MISSING_KYCS, location.pathname),
      count: missingKycsCountV2,
    });

    return tabs;
  };

  const renderContent = () => {
    if (!!matchPath(PATH_OPEN_PAYMENTS, location.pathname)) {
      return <OpenPaymentRequests />;
    }

    if (!!matchPath(PATH_UNMATCHED_PAY_INS, location.pathname)) {
      return <PayInsFollowUp />;
    }

    if (!!matchPath(PATH_MISSING_KYCS, location.pathname)) {
      return <CollectionFollowUpV2 />;
    }

    return <OpenPaymentRequests />;
  };

  return (
    <Page
      title={ts.paymentsFollowUpHeading()}
      metadata={
        <>
          {ts.followUpPaymentsPageDescription()}

          {!matchPath(PATH_MISSING_KYCS, location.pathname) &&
            isFetchingMissingKycsCountV2 &&
            (missingKycsCountV2 || 0) === 0 && <Loading />}

          {!matchPath(PATH_MISSING_KYCS, location.pathname) &&
            (missingKycsCountV2 || 0) > 0 && (
              <>
                <Spacer weight={ESpacerWeight.W16} />
                <Banner
                  title={getLocalizedText(
                    "payments.follow_up.kyc.banner.title",
                    {
                      value: `${missingKycsCountV2 || 0}`,
                    },
                  )}
                  icon="alertDiamond"
                  variation="warning"
                  hasDismiss={false}
                >
                  <TextStyle>
                    {getLocalizedText("payments.follow_up.kyc.banner.content", {
                      value: `${missingKycsCountV2}`,
                    })}
                  </TextStyle>

                  <Spacer weight={ESpacerWeight.W08} />

                  <Link to="/follow-up/payments/missing-kycs">
                    {getLocalizedText("payments.follow_up.kyc.banner.link")}
                  </Link>
                </Banner>
              </>
            )}
        </>
      }
      fullWidth
      tabs={getTabs()}
    >
      {renderContent()}
    </Page>
  );
};
